import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Current round number (1-indexed). */
  roundCount: number;
  /** Maximum rounds if the session caps them. */
  maxRounds?: number | null;
  /** Cooldown in seconds from the last server poll. */
  cooldownSecondsRemaining: number;
}

/**
 * BuffetHud — slim status bar shown in buffet sessions.
 * Displays the round counter and a client-side countdown that ticks down from
 * the server's cooldown_seconds_remaining value. The countdown is display-only:
 * the server enforces the cooldown; this is a UX convenience only.
 */
export default function BuffetHud({
  roundCount,
  maxRounds,
  cooldownSecondsRemaining,
}: Props) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(cooldownSecondsRemaining);

  // Re-sync countdown when the server pushes a fresh value (poll cadence).
  useEffect(() => {
    setCountdown(cooldownSecondsRemaining);
  }, [cooldownSecondsRemaining]);

  // Tick down client-side.
  useEffect(() => {
    if (countdown <= 0) return;
    const id = window.setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [countdown]);

  const roundLabel =
    maxRounds != null
      ? t('buffetHud.roundWithMax', { n: roundCount, max: maxRounds })
      : t('buffetHud.round', { n: roundCount });

  return (
    <div
      className="flex items-center gap-3 px-3 py-1.5 bg-mint/20 border border-mint rounded-sm font-mono text-sm"
      data-testid="buffet-hud"
    >
      <span className="font-display">{roundLabel}</span>
      {countdown > 0 && (
        <span className="text-ink/70">
          {t('buffetHud.cooldown', { seconds: countdown })}
        </span>
      )}
    </div>
  );
}
